import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Breadcrumbs,
  Link,
  InputAdornment,
  CircularProgress,
  Alert,
  Avatar,
  IconButton,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { Loading } from "../../components/Loading";
import { useToast } from "../../components/Toast";
import { uploadFile } from "../../utils/UploadFile";
import { fetchDocuments } from "../../utils/FetchDocuments";
import { LocalUser } from "../../utils/UploadFile";
import { UploadFile } from "@mui/icons-material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getValidToken } from "../../utils/authUtils";
import NavigationHeader from "../../components/NavigationHeader";
import {
  commonButtonProps,
  commonTextFieldProps,
  smallButtonProp,
} from "../../utils/styles";
import { usePlanStatus } from "../../hooks/usePlanStatus";

interface StaffFormData {
  name: string;
  contactNumber: string;
  alternateNumber: string;
  address: string;
  type: "DRIVER" | "CLEANER" | "LADY" | "SUPERVISOR" | "OTHER";
  status: "ACTIVE" | "INACTIVE" | "BLACKLISTED";
  employmentType: EmploymentType;
  joiningDate: string;
  salary: number;
  notes: string;
  aadharNumber?: string | undefined;
  drivingLicense?: string | undefined;
  referredBy: {
    name: string;
    contactNumber: string;
  };
  references: {
    name: string;
    contactNumber: string;
  }[];
  documents: {
    aadharCard?: string;
    drivingLicense?: string;
    photo?: string;
    otherDocuments?: string[];
  };
}

interface DocumentItem {
  file: File | null;
  url: string;
  id: string;
  isNew: boolean;
  description?: string;
  fileName?: string;
}

const validationSchema = Yup.object({
  name: Yup.string()
    .min(3, "Name must be at least 3 characters")
    .required("Name is required"),
  contactNumber: Yup.string()
    .matches(/^[0-9]{10}$/, "Contact number must be 10 digits")
    .required("Contact number is required"),
  alternateNumber: Yup.string()
    .matches(/^[0-9]{10}$/, "Alternate number must be 10 digits")
    .optional(),
  address: Yup.string(),
  type: Yup.string()
    .oneOf(
      ["DRIVER", "CLEANER", "LADY", "SUPERVISOR", "OTHER"],
      "Invalid staff type"
    )
    .required("Staff type is required"),
  status: Yup.string()
    .oneOf(["ACTIVE", "INACTIVE", "BLACKLISTED"], "Invalid status")
    .required("Status is required"),
  employmentType: Yup.string()
    .oneOf(["PERMANENT", "TEMPORARY", "OUTSOURCED"], "Invalid employment type")
    .required("Employment type is required"),
  aadharNumber: Yup.string()
    .required("Aadhar number is required")
    .matches(/^[0-9]{12}$/, "Aadhar number must be 12 digits"),
  referredBy: Yup.object()
    .shape({
      name: Yup.string().optional(),
      contactNumber: Yup.string()
        .optional()
        .test(
          "valid-contact",
          "Contact number must be 10 digits",
          function (value) {
            if (!value) return true; // Allow empty values
            return /^[0-9]{10}$/.test(value);
          }
        ),
    })
    .optional(),
  references: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().optional(),
        contactNumber: Yup.string()
          .optional()
          .test(
            "valid-contact",
            "Contact number must be 10 digits",
            function (value) {
              if (!value) return true; // Allow empty values
              return /^[0-9]{10}$/.test(value);
            }
          ),
      })
    )
    .optional(),
  notes: Yup.string(),
  documents: Yup.object({
    aadharCard: Yup.string(),
    drivingLicense: Yup.string(),
    photo: Yup.string(),
  }),
});

type EmploymentType = "PERMANENT" | "TEMPORARY" | "OUTSOURCED";

const employmentTypeOptions = [
  { label: "Permanent", value: "PERMANENT" },
  { label: "Temporary", value: "TEMPORARY" },
  { label: "Outsourced", value: "OUTSOURCED" },
];

const StaffForm = () => {
  const { type, id } = useParams();
  const typeUpper = type?.toLocaleUpperCase();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string>("");
  const [photoId, setPhotoId] = useState<string>("");
  const [aadharDocuments, setAadharDocuments] = useState<DocumentItem[]>([]);
  const [drivingLicenseDocuments, setDrivingLicenseDocuments] = useState<
    DocumentItem[]
  >([]);
  const [otherDocuments, setOtherDocuments] = useState<DocumentItem[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const aadharInputRef = useRef<HTMLInputElement>(null);
  const drivingLicenseInputRef = useRef<HTMLInputElement>(null);
  const [blacklistDialogOpen, setBlacklistDialogOpen] = useState(false);
  const [initialValues, setInitialValues] = useState<StaffFormData>({
    name: "",
    contactNumber: "",
    alternateNumber: "",
    address: "",
    type: typeUpper as "DRIVER" | "CLEANER" | "LADY" | "SUPERVISOR" | "OTHER",
    status: "ACTIVE",
    employmentType: employmentTypeOptions[0].value as EmploymentType,
    joiningDate: new Date().toISOString().split("T")[0],
    salary: 0,
    notes: "",
    referredBy: {
      name: "",
      contactNumber: "",
    },
    references: [
      {
        name: "",
        contactNumber: "",
      },
      {
        name: "",
        contactNumber: "",
      },
    ],
    documents: {},
  });
  const otherDocumentInputRef = useRef<HTMLInputElement>(null);

  const fetchStaffPhoto = async () => {
    try {
      const documents = await fetchDocuments({
        documentContext: "staff",
        documentTypeId: id || "",
        documentType: "staff-photo",
      });

      if (documents && documents.length > 0) {
        setPhotoUrl(documents[0].presignedUrl);
        setPhotoId(documents[0].id);
      }
    } catch (error) {
      console.error("Error fetching staff photo:", error);
    }
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setSelectedPhotoFile(file);
    }
  };

  const handlePhotoRemove = () => {
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhotoPreview(null);
    setSelectedPhotoFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelection = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "aadhar" | "drivingLicense" | "otherDocument",
    index?: number
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newDocuments = Array.from(files).map((file) => ({
      file,
      url: URL.createObjectURL(file),
      id: "",
      isNew: true,
      description: type === "otherDocument" ? "" : undefined,
    }));

    if (type === "aadhar") {
      if (typeof index === "number") {
        setAadharDocuments((prev) => {
          const updated = [...prev];
          updated[index] = newDocuments[0];
          return updated;
        });
      } else {
        setAadharDocuments((prev) => [...prev, ...newDocuments]);
      }
    } else if (type === "drivingLicense") {
      if (typeof index === "number") {
        setDrivingLicenseDocuments((prev) => {
          const updated = [...prev];
          updated[index] = newDocuments[0];
          return updated;
        });
      } else {
        setDrivingLicenseDocuments((prev) => [...prev, ...newDocuments]);
      }
    } else if (type === "otherDocument") {
      if (typeof index === "number") {
        setOtherDocuments((prev) => {
          const updated = [...prev];
          updated[index] = newDocuments[0];
          return updated;
        });
      } else {
        setOtherDocuments((prev) => [...prev, ...newDocuments]);
      }
    }
  };

  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "aadhar" | "drivingLicense" | "otherDocument",
    index: number
  ) => {
    handleFileSelection(event, type, index);
  };

  const uploadDocuments = async (documentId: string) => {
    try {
      const uploadPromises: any = [];

      // Handle Aadhar documents
      aadharDocuments.forEach((doc) => {
        if (doc.file) {
          uploadPromises.push(
            uploadFile({
              file: doc.file,
              documentType: "staff-aadhar",
              documentTypeId: documentId,
              documentContext: "staff",
              description: "Aadhar Card",
              companyId: sessionStorage.getItem("companyId") || "",
              ...(doc.id && {
                documentId: doc.id,
                isUpdate: true,
              }),
            })
          );
        }
      });

      // Handle Driving License documents
      drivingLicenseDocuments.forEach((doc) => {
        if (doc.file) {
          uploadPromises.push(
            uploadFile({
              file: doc.file,
              documentType: "staff-driving-license",
              documentTypeId: documentId,
              documentContext: "staff",
              description: "Driving License",
              companyId: sessionStorage.getItem("companyId") || "",
              ...(doc.id && {
                documentId: doc.id,
                isUpdate: true,
              }),
            })
          );
        }
      });

      await Promise.all(uploadPromises);
    } catch (error) {
      console.error("Error uploading documents:", error);
      showToast(`Failed to upload ${type} documents`, "error");
      throw error;
    }
  };

  const uploadOtherDocuments = async (documentId: string) => {
    try {
      const uploadPromises: any = [];
      // Handle Other documents
      otherDocuments.forEach((doc, index) => {
        if (doc.file) {
          uploadPromises.push(
            uploadFile({
              file: doc.file,
              documentType: "staff-other-document",
              documentTypeId: documentId,
              documentContext: "staff",
              description: doc.description || "",
              companyId: sessionStorage.getItem("companyId") || "",
              ...(doc.id && {
                documentId: doc.id,
                isUpdate: true,
              }),
            })
          );
        }
      });
      await Promise.all(uploadPromises);
    } catch (error) {
      console.error("Error uploading documents:", error);
      showToast("Failed to upload documents", "error");
      throw error;
    }
  };

  const fetchStaffDocuments = async () => {
    if (!id) return;

    try {
      const [aadharDocs, drivingLicenseDocs, otherDocs] = await Promise.all([
        fetchDocuments({
          documentContext: "staff",
          documentTypeId: id,
          documentType: "staff-aadhar",
        }),
        fetchDocuments({
          documentContext: "staff",
          documentTypeId: id,
          documentType: "staff-driving-license",
        }),
        fetchDocuments({
          documentContext: "staff",
          documentTypeId: id,
          documentType: "staff-other-document",
        }),
      ]);

      if (aadharDocs && aadharDocs.length > 0) {
        setAadharDocuments(
          aadharDocs.map((doc) => ({
            file: null,
            url: doc?.presignedUrl,
            id: doc?.id,
            isNew: false,
            fileName: doc?.fileName,
          }))
        );
      }

      if (drivingLicenseDocs && drivingLicenseDocs.length > 0) {
        setDrivingLicenseDocuments(
          drivingLicenseDocs.map((doc) => ({
            file: null,
            url: doc?.presignedUrl,
            id: doc?.id,
            isNew: false,
            fileName: doc?.fileName,
          }))
        );
      }

      if (otherDocs && otherDocs.length > 0) {
        setOtherDocuments(
          otherDocs.map((doc) => ({
            file: null,
            url: doc?.presignedUrl,
            id: doc?.id,
            isNew: false,
            description: doc?.description || "",
            fileName: doc?.fileName,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching staff documents:", error);
    }
  };

  const fetchStaffMember = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const token = await getValidToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}staff-members/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.isSuccess) {
        const staffData = response.data.data;
        // Transform API data to match form structure
        setInitialValues({
          name: staffData.name,
          contactNumber: staffData.contactNumber,
          alternateNumber: staffData.alternateNumber || "",
          address: staffData.address,
          type: staffData.type,
          status: staffData.status,
          employmentType: staffData.employmentType,
          joiningDate: staffData.joiningDate
            ? new Date(staffData.joiningDate).toISOString().split("T")[0]
            : "",
          salary: parseFloat(staffData.salary) || 0,
          notes: staffData.notes || "",
          aadharNumber: staffData.aadharNumber,
          drivingLicense: staffData.drivingLicense || "",
          referredBy: {
            name: staffData.referredByName || "",
            contactNumber: staffData.referredByContact || "",
          },
          references: [
            {
              name: staffData.reference1Name || "",
              contactNumber: staffData.reference1Contact || "",
            },
            {
              name: staffData.reference2Name || "",
              contactNumber: staffData.reference2Contact || "",
            },
          ],
          documents: {
            photo: staffData.photoUrl || "",
          },
        });

        if (staffData.photoUrl) {
          setPhotoUrl(staffData.photoUrl);
        }
      }
    } catch (error) {
      setError("Failed to fetch staff member details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    fetchStaffMember();
    fetchStaffPhoto();
    fetchStaffDocuments();
  }, [id]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        setError(null);

        // Transform the data to match backend's expected format
        const { referredBy, references, aadharNumber, ...restValues } = values;

        const transformedData = {
          ...restValues,
          ...(aadharNumber !== "" && { aadharNumber }),
          referredByName: referredBy.name,
          referredByContact: referredBy.contactNumber,
          reference1Name: references[0].name,
          reference1Contact: references[0].contactNumber,
          reference2Name: references[1].name,
          reference2Contact: references[1].contactNumber,
          documents: {
            ...values.documents,
            photo: values.documents.photo || "",
          },
        };

        const url = id
          ? `${process.env.REACT_APP_API_HOST}staff-members/${id}`
          : `${process.env.REACT_APP_API_HOST}staff-members`;

        const method = id ? "put" : "post";
        const token = await getValidToken();
        const response = await axios[method](url, transformedData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data?.isSuccess) {
          // Upload photo if selected
          if (selectedPhotoFile) {
            try {
              await uploadFile({
                file: selectedPhotoFile,
                documentType: "staff-photo",
                documentTypeId:
                  method === "post" ? response?.data?.data?.id : id,
                documentContext: "staff",
                description: "Staff photo",
                companyId: sessionStorage.getItem("companyId") || "",
                ...(photoId && {
                  documentId: photoId,
                  isUpdate: true,
                }),
              });
            } catch (error) {
              console.error("Error uploading photo:", error);
              showToast("Failed to upload photo", "error");
            }
          }

          // Upload other documents
          await uploadDocuments(response?.data?.data?.id || id);
          // Upload other documents with tags
          await uploadOtherDocuments(response?.data?.data?.id || id);

          showToast(`${type} saved successfully`, "success");
          navigate(`/master-data/staff/${typeUpper}`);
        } else {
          throw new Error("Failed to save staff member");
        }
      } catch (error: any) {
        console.error("Error saving staff member:", error);
        setError(
          error.response?.data?.message ||
            "Failed to save staff member. Please try again."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // useEffect(() => {
  //   return () => {
  //     if (aadharPreview) URL.revokeObjectURL(aadharPreview);
  //     if (drivingLicensePreview) URL.revokeObjectURL(drivingLicensePreview);
  //   };
  // }, [aadharPreview, drivingLicensePreview]);

  const { planStatus } = usePlanStatus();

  const canBlacklist =
    typeUpper === "CLEANER"
      ? planStatus?.permissions?.canBlacklistCleaner
      : typeUpper === "DRIVER"
      ? planStatus?.permissions?.canBlacklistDriver
      : typeUpper === "LADY"
      ? planStatus?.permissions?.canBlacklistLadyAttendant
      : typeUpper === "SUPERVISOR"
      ? planStatus?.permissions?.canBlacklistSupervisor
      : false;

  if (isLoading && id) {
    return <Loading message={`Loading ${type} details...`} />;
  }

  if (isSubmitting) {
    return <Loading message="Submitting..." />;
  }

  const handlePreview = (url: string) => {
    window.open(url, "_blank");
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <NavigationHeader
        title={
          id ? `Edit ${type?.toLowerCase()}` : `Add New ${type?.toLowerCase()}`
        }
        breadcrumbs={[
          {
            label: "Master Data",
            path: "/master-data",
          },
          {
            label: "Driver/Cleaner",
            path: `/master-data/staff/${typeUpper}`,
          },
          {
            label: id
              ? `Edit ${type?.toLowerCase()}`
              : `Add ${type?.toLowerCase()}`,
          },
        ]}
      />

      <form onSubmit={formik.handleSubmit}>
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle1" gutterBottom>
              {id
                ? `Edit ${type?.toLowerCase()}`
                : `Add New ${type?.toLowerCase()}`}
            </Typography>
            {id &&
              canBlacklist &&
              (formik.values.status !== "BLACKLISTED" ? (
                <Button
                  variant="outlined"
                  color="error"
                  {...smallButtonProp}
                  onClick={() => {
                    setBlacklistDialogOpen(true);
                  }}
                >
                  Blacklist
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="error"
                  {...smallButtonProp}
                  onClick={() => {
                    formik.setFieldValue("status", "ACTIVE");
                    formik.handleSubmit();
                  }}
                >
                  Unblacklist
                </Button>
              ))}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  src={photoPreview || photoUrl || ""}
                  sx={{
                    width: 80,
                    height: 80,
                    mr: 2,
                    border: "2px solid #e0e0e0",
                  }}
                  imgProps={{
                    onError: (e) => {
                      console.error("Error loading image:", e);
                    },
                  }}
                />
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="photo-upload"
                    type="file"
                    onChange={handlePhotoChange}
                    ref={fileInputRef}
                  />
                  <label htmlFor="photo-upload">
                    <IconButton
                      color="primary"
                      aria-label="upload staff photo"
                      component="span"
                    >
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  {photoPreview && (
                    <Button
                      size="small"
                      color="error"
                      onClick={handlePhotoRemove}
                      sx={{ ml: 1 }}
                    >
                      Remove
                    </Button>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {photoPreview ? "Change Photo" : `Upload ${type} Photo`}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid container item xs={12} sm={8} spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  required
                  {...commonTextFieldProps}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth {...commonTextFieldProps}>
                  <InputLabel shrink sx={{ fontSize: "0.75rem" }} required>
                    Employment Type
                  </InputLabel>
                  <Select
                    name="employmentType"
                    label="Employment Type"
                    value={formik.values.employmentType}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.employmentType &&
                      Boolean(formik.errors.employmentType)
                    }
                    displayEmpty
                    notched
                    required
                    {...commonTextFieldProps.SelectProps}
                  >
                    {employmentTypeOptions.map((option) => (
                      <MenuItem
                        value={option.value}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error>
                    {formik.touched.employmentType &&
                      formik.errors.employmentType}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth {...commonTextFieldProps}>
                  <InputLabel shrink sx={{ fontSize: "0.75rem" }}>
                    Status
                  </InputLabel>
                  <Select
                    name="status"
                    label="Status"
                    value={formik.values.status}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.status && Boolean(formik.errors.status)
                    }
                    displayEmpty
                    notched
                    {...commonTextFieldProps.SelectProps}
                  >
                    <MenuItem value="ACTIVE" {...commonTextFieldProps}>
                      Active
                    </MenuItem>
                    <MenuItem value="INACTIVE" {...commonTextFieldProps}>
                      Inactive
                    </MenuItem>
                    <MenuItem value="BLACKLISTED" {...commonTextFieldProps}>
                      Blacklisted
                    </MenuItem>
                  </Select>
                  <FormHelperText error>
                    {formik.touched.status && formik.errors.status}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Number"
                  name="contactNumber"
                  value={formik.values.contactNumber}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.contactNumber &&
                    Boolean(formik.errors.contactNumber)
                  }
                  helperText={
                    formik.touched.contactNumber && formik.errors.contactNumber
                  }
                  inputProps={{ maxLength: 10 }}
                  required
                  {...commonTextFieldProps}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Alternate Number"
                  name="alternateNumber"
                  value={formik.values.alternateNumber}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.alternateNumber &&
                    Boolean(formik.errors.alternateNumber)
                  }
                  helperText={
                    formik.touched.alternateNumber &&
                    formik.errors.alternateNumber
                  }
                  inputProps={{
                    maxLength: 10,
                  }}
                  placeholder="Enter 10 digit number"
                  {...commonTextFieldProps}
                />
              </Grid>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Aadhar Number"
                name="aadharNumber"
                value={formik.values.aadharNumber}
                onChange={formik.handleChange}
                error={
                  formik.touched.aadharNumber &&
                  Boolean(formik.errors.aadharNumber)
                }
                helperText={
                  formik.touched.aadharNumber && formik.errors.aadharNumber
                }
                inputProps={{ maxLength: 12 }}
                InputProps={{
                  inputMode: "numeric",
                }}
                required
                {...commonTextFieldProps}
              />
            </Grid>

            {formik.values.type === "DRIVER" && (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Driving License Number"
                  name="drivingLicense"
                  value={formik.values.drivingLicense}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.drivingLicense &&
                    Boolean(formik.errors.drivingLicense)
                  }
                  helperText={
                    formik.touched.drivingLicense &&
                    formik.errors.drivingLicense
                  }
                  {...commonTextFieldProps}
                />
              </Grid>
            )}

            {formik.values.employmentType === "PERMANENT" && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Joining Date"
                    name="joiningDate"
                    value={formik.values.joiningDate}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.joiningDate &&
                      Boolean(formik.errors.joiningDate)
                    }
                    helperText={
                      formik.touched.joiningDate && formik.errors.joiningDate
                    }
                    InputLabelProps={{ shrink: true }}
                    {...commonTextFieldProps}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Salary"
                    name="salary"
                    value={formik.values.salary}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.salary && Boolean(formik.errors.salary)
                    }
                    helperText={formik.touched.salary && formik.errors.salary}
                    inputProps={{
                      maxLength: 7,
                      inputMode: "numeric",
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                    {...commonTextFieldProps}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                multiline
                rows={2}
                value={formik.values.address}
                onChange={formik.handleChange}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                {...commonTextFieldProps}
              />
            </Grid>
          </Grid>
        </Paper>

        {formik.values.type !== "LADY" &&
          formik.values.type !== "SUPERVISOR" && (
            <Paper sx={{ p: 2, my: 2 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Referred By
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Referrer Name"
                      name="referredBy.name"
                      value={formik.values.referredBy.name}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.referredBy?.name &&
                        Boolean(formik.errors.referredBy?.name)
                      }
                      helperText={
                        formik.touched.referredBy?.name &&
                        (formik.errors.referredBy as any)?.name
                      }
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Referrer Contact Number"
                      name="referredBy.contactNumber"
                      value={formik.values.referredBy.contactNumber}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.referredBy?.contactNumber &&
                        Boolean(formik.errors.referredBy?.contactNumber)
                      }
                      helperText={
                        formik.touched.referredBy?.contactNumber &&
                        (formik.errors.referredBy as any)?.contactNumber
                      }
                      inputProps={{ maxLength: 10 }}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography variant="subtitle1" gutterBottom>
                  References
                </Typography>
                <Grid container spacing={2}>
                  {[0, 1].map((index) => (
                    <React.Fragment key={index}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                          Reference {index + 1}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Name"
                          name={`references.${index}.name`}
                          value={formik.values.references[index].name}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.references?.[index]?.name &&
                            Boolean(
                              (formik.errors.references?.[index] as any)?.name
                            )
                          }
                          helperText={
                            formik.touched.references?.[index]?.name &&
                            (formik.errors.references?.[index] as any)?.name
                          }
                          {...commonTextFieldProps}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Contact Number"
                          name={`references.${index}.contactNumber`}
                          value={formik.values.references[index].contactNumber}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.references?.[index]?.contactNumber &&
                            Boolean(
                              (formik.errors.references?.[index] as any)
                                ?.contactNumber
                            )
                          }
                          helperText={
                            formik.touched.references?.[index]?.contactNumber &&
                            (formik.errors.references?.[index] as any)
                              ?.contactNumber
                          }
                          inputProps={{ maxLength: 10 }}
                          {...commonTextFieldProps}
                        />
                      </Grid>
                    </React.Fragment>
                  ))}
                </Grid>
              </Grid>
            </Paper>
          )}

        <Paper sx={{ p: 2, my: 2 }}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Upload Documents
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Driving License
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1 }}
                  >
                    Please upload both front and back images of the driving
                    license
                  </Typography>
                  {drivingLicenseDocuments.length > 0 ? (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {drivingLicenseDocuments.map((doc, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            p: 1,
                            width: "100%",
                            maxWidth: 400,
                          }}
                        >
                          <InsertDriveFileIcon
                            color="action"
                            sx={{ fontSize: "1rem" }}
                          />
                          <Box
                            sx={{
                              flexGrow: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "0.75rem",
                            }}
                          >
                            {doc.file?.name ||
                              doc?.fileName ||
                              `Driving License ${index + 1}.png`}
                          </Box>
                          <IconButton onClick={() => handlePreview(doc.url)}>
                            <VisibilityIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                          <Button
                            variant="text"
                            component="label"
                            {...commonButtonProps}
                          >
                            Change
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              multiple
                              onChange={(e) =>
                                handleImageChange(e, "drivingLicense", index)
                              }
                              ref={drivingLicenseInputRef}
                            />
                          </Button>
                        </Box>
                      ))}
                      <Button
                        variant="outlined"
                        startIcon={<UploadFile sx={{ fontSize: "1rem" }} />}
                        component="label"
                        {...commonButtonProps}
                        style={{
                          maxWidth: 400,
                        }}
                      >
                        Add More
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          multiple
                          onChange={(e) =>
                            handleFileSelection(e, "drivingLicense")
                          }
                          ref={drivingLicenseInputRef}
                        />
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<UploadFile sx={{ fontSize: "1rem" }} />}
                      component="label"
                      {...commonButtonProps}
                    >
                      Upload Driving License
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        multiple
                        onChange={(e) =>
                          handleFileSelection(e, "drivingLicense")
                        }
                        ref={drivingLicenseInputRef}
                      />
                    </Button>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Aadhar Card
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1 }}
                  >
                    Please upload both front and back images of the Aadhar card
                  </Typography>
                  {aadharDocuments.length > 0 ? (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {aadharDocuments.map((doc, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            p: 1,
                            width: "100%",
                            maxWidth: 400,
                          }}
                        >
                          <InsertDriveFileIcon
                            color="action"
                            sx={{ fontSize: "1rem" }}
                          />
                          <Box
                            sx={{
                              flexGrow: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "0.75rem",
                            }}
                          >
                            {doc.file?.name ||
                              doc?.fileName ||
                              `Aadhar Card ${index + 1}.png`}
                          </Box>
                          <IconButton onClick={() => handlePreview(doc.url)}>
                            <VisibilityIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                          <Button
                            variant="text"
                            component="label"
                            {...commonButtonProps}
                          >
                            Change
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              multiple
                              onChange={(e) =>
                                handleImageChange(e, "aadhar", index)
                              }
                              ref={aadharInputRef}
                            />
                          </Button>
                        </Box>
                      ))}
                      <Button
                        variant="outlined"
                        startIcon={<UploadFile sx={{ fontSize: "1rem" }} />}
                        component="label"
                        {...commonButtonProps}
                        style={{
                          maxWidth: 400,
                        }}
                      >
                        Add More
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          multiple
                          onChange={(e) => handleFileSelection(e, "aadhar")}
                          ref={aadharInputRef}
                        />
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<UploadFile sx={{ fontSize: "1rem" }} />}
                      component="label"
                      {...commonButtonProps}
                    >
                      Upload Aadhar Card
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        multiple
                        onChange={(e) => handleFileSelection(e, "aadhar")}
                        ref={aadharInputRef}
                      />
                    </Button>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sm={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Other Documents
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1 }}
                  >
                    Upload any additional documents (e.g., certificates,
                    experience letters)
                  </Typography>
                  {otherDocuments.length > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      {otherDocuments.map((doc, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            p: 1,
                            width: "100%",
                          }}
                        >
                          <InsertDriveFileIcon
                            color="action"
                            sx={{ fontSize: "1rem" }}
                          />
                          <Box
                            sx={{
                              flexGrow: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "0.75rem",
                            }}
                          >
                            {doc.file?.name ||
                              doc?.fileName ||
                              `Other Document ${index + 1}.png`}
                          </Box>
                          {Boolean(id) && doc.id ? (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Document Type
                              </Typography>
                              <Typography variant="body2">
                                {doc.description || "N/A"}
                              </Typography>
                            </Box>
                          ) : (
                            <TextField
                              label="Document Type"
                              placeholder="Add document type"
                              value={doc.description || ""}
                              onChange={(e) => {
                                const updatedDocs = [...otherDocuments];
                                updatedDocs[index] = {
                                  ...doc,
                                  description: e.target.value,
                                };
                                setOtherDocuments(updatedDocs);
                              }}
                              {...commonTextFieldProps}
                            />
                          )}
                          <IconButton onClick={() => handlePreview(doc.url)}>
                            <VisibilityIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                          <Button
                            variant="text"
                            component="label"
                            {...commonButtonProps}
                          >
                            Change
                            <input
                              type="file"
                              hidden
                              accept="image/*,.pdf"
                              multiple
                              onChange={(e) =>
                                handleImageChange(e, "otherDocument", index)
                              }
                              ref={otherDocumentInputRef}
                            />
                          </Button>
                        </Box>
                      ))}
                      <Button
                        variant="outlined"
                        startIcon={<UploadFile sx={{ fontSize: "1rem" }} />}
                        component="label"
                        {...commonButtonProps}
                        style={{
                          maxWidth: 400,
                        }}
                      >
                        Add More
                        <input
                          type="file"
                          hidden
                          accept="image/*,.pdf"
                          multiple
                          onChange={(e) =>
                            handleFileSelection(e, "otherDocument")
                          }
                          ref={otherDocumentInputRef}
                        />
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<UploadFile sx={{ fontSize: "1rem" }} />}
                      component="label"
                      {...commonButtonProps}
                    >
                      Upload Document
                      <input
                        type="file"
                        hidden
                        accept="image/*,.pdf"
                        multiple
                        onChange={(e) =>
                          handleFileSelection(e, "otherDocument")
                        }
                        ref={otherDocumentInputRef}
                      />
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        <Box
          sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate(`/master-data/staff/${typeUpper}`)}
            {...commonButtonProps}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting && <CircularProgress size={20} />}
            {...commonButtonProps}
          >
            {id ? "Update" : "Save"}
          </Button>
        </Box>
        <Dialog
          open={blacklistDialogOpen}
          onClose={() => setBlacklistDialogOpen(false)}
        >
          <DialogTitle>Remark</DialogTitle>
          <DialogContent>
            <Typography variant="subtitle1" gutterBottom>
              Please provide a reason for blacklisting the staff member.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              name="notes"
              value={formik.values.notes}
              onChange={formik.handleChange}
              placeholder="Enter reason for blacklisting"
              inputProps={{
                maxLength: 300,
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBlacklistDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                formik.setFieldValue("status", "BLACKLISTED");
                formik.handleSubmit();
              }}
              color="error"
            >
              Blacklist
            </Button>
          </DialogActions>
        </Dialog>
      </form>
    </Container>
  );
};

export default StaffForm;
